import { useState } from 'react';
import { toast } from 'sonner';
import { putDistributionRules, useDistributionRules, useRefreshStores, type DistributionRules, type StoreApp } from '@/api/endpointsStores';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { EnvPill } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { showApiFailure } from '@/features/access/accessToasts';
import { stagger } from '@/lib/motion';
import { ruleErrors, ruleRowsOf, rulesBodyOf, rulesChanged, showsRolloutCap, type ProjectEnvironment, type RuleRow, type StoresAccess } from './storesModel';

interface Props {
  appId: string;
  storeApps: StoreApp[];
  environments: ProjectEnvironment[];
  access: StoresAccess;
}

/**
 * "Rules" (P29 phase 1, SPEC §39.4): the tracks each environment's builds may reach in each Google Play app, and the
 * largest first rollout. A guardrail, so admins and platform operators change it; a submit outside it is refused.
 */
export function RulesCard({ appId, storeApps, environments, access }: Props) {
  const rules = useDistributionRules(appId);
  return (
    <Card className="enter min-w-0" style={stagger(1)} data-testid="stores-rules-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="rule" size={18} className="text-subtle" />
          Rules
        </CardTitle>
        <CardDescription>The tracks each environment&apos;s builds may reach. An environment without tracks never submits.</CardDescription>
      </CardHeader>
      {rules.isLoading && !rules.data ? (
        <CardContent className="flex flex-col gap-2" aria-busy="true" aria-label="Loading the rules">
          {[0, 1].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </CardContent>
      ) : rules.isError && !rules.data ? (
        <CardContent><ErrorState compact error={rules.error} onRetry={() => void rules.refetch()} /></CardContent>
      ) : (
        <RulesForm key={rules.data!.etag} appId={appId} saved={rules.data!} storeApps={storeApps} environments={environments} access={access} />
      )}
    </Card>
  );
}

function useSaveRules(appId: string) {
  const refresh = useRefreshStores(appId);
  const [pending, setPending] = useState(false);
  const save = async (rows: RuleRow[], etag: string) => {
    setPending(true);
    try {
      await putDistributionRules(appId, rulesBodyOf(rows), etag);
      toast.success('Rules saved', { description: 'Submits outside these tracks are refused.' });
    } catch (err) {
      showApiFailure(err, 'Could not save the rules');
    } finally {
      setPending(false);
      refresh();
    }
  };
  return { save, pending };
}

function RulesForm({ appId, saved, storeApps, environments, access }: Props & { saved: DistributionRules }) {
  const initial = ruleRowsOf(storeApps, environments, saved.rules);
  const [rows, setRows] = useState(initial);
  const [touched, setTouched] = useState(false);
  const { save, pending } = useSaveRules(appId);
  const errors = ruleErrors(rows);
  const shown = touched ? errors : {};
  const set = (key: string, patch: Partial<RuleRow>) => setRows((all) => all.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const changed = rulesChanged(rows, initial);
  const submit = () => {
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    void save(rows, saved.etag);
  };
  const readOnly = !access.editRules.allowed;
  return (
    <>
      <CardContent className="flex flex-col gap-5">
        {storeApps.map((s) => (
          <fieldset key={s.id} className="flex min-w-0 flex-col gap-2" data-testid={`stores-rules-${s.identifier}`}>
            <legend className="mb-1 font-mono text-[13px] font-medium text-foreground">{s.identifier}</legend>
            {rows.filter((r) => r.storeAppId === s.id).map((r) => <RuleRowFields key={r.key} row={r} error={shown[r.key]} readOnly={readOnly} onChange={(patch) => set(r.key, patch)} />)}
          </fieldset>
        ))}
      </CardContent>
      {access.editRules.hidden ? null : (
        <CardFooter className="justify-end gap-2">
          {changed ? <Button variant="ghost" onClick={() => { setRows(initial); setTouched(false); }} className="max-sm:h-11">Discard</Button> : null}
          <GatedButton data-testid="stores-rules-save" can={access.editRules} blockedReason={changed ? undefined : 'Nothing changed'} loading={pending} onClick={submit} className="max-sm:h-11">
            Save rules
          </GatedButton>
        </CardFooter>
      )}
    </>
  );
}

function RuleRowFields({ row, error, readOnly, onChange }: { row: RuleRow; error: string | undefined; readOnly: boolean; onChange: (patch: Partial<RuleRow>) => void }) {
  const id = `stores-rule-${row.env.slug}`;
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-lg border px-3 py-2.5" data-testid={id}>
      <div className="grid min-w-0 items-center gap-2 sm:grid-cols-[minmax(0,9rem)_minmax(0,1fr)_auto]">
        <EnvPill env={row.env} className="justify-self-start" />
        <Input data-testid={`${id}-tracks`} value={row.tracks} readOnly={readOnly} onChange={(e) => onChange({ tracks: e.target.value })} placeholder="No submits"
          className="h-8 font-mono max-sm:h-10 sm:text-[13px]" spellCheck={false} aria-label={`Tracks ${row.env.name} may reach`} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-error` : undefined} />
        {showsRolloutCap(row) ? (
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground" title="The largest share of users a first production release reaches">
            <span className="whitespace-nowrap">First production rollout ≤</span>
            <Input data-testid={`${id}-rollout`} inputMode="numeric" value={row.rolloutPercent} readOnly={readOnly} onChange={(e) => onChange({ rolloutPercent: e.target.value.replace(/[^0-9]/g, '') })} placeholder="100"
              className="h-8 w-14 text-right font-mono max-sm:h-10 sm:text-[13px]" aria-label={`Largest first production rollout percent for ${row.env.name}`} />
            <span>%</span>
          </label>
        ) : null}
      </div>
      {error ? <p id={`${id}-error`} className="text-xs text-destructive" data-testid={`${id}-error`}>{error}</p> : null}
    </div>
  );
}
